import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { MAX_NOTE_FILES } from './note-files';
import {
  FilePickers,
  FileRow,
  FilesBox,
  PendingRows,
  RefusedRows,
  SendErrors,
  useSavedDetail,
} from './NoteFileParts';
import type { Report } from './types';
import { openNoteFile, useNoteFilesPanel } from './use-note-files';

const OPEN_STATUSES: readonly string[] = ['draft', 'more_info_requested'];

/**
 * «Documenti della nota» on the note's page (U1 board 1.4, 1.6, 1.7). The owner changes them
 * while the note is a draft or sent back; Gestione adds them in any status, a closed month
 * included, and after submission removes only the files it added. The server's `can` decides.
 */
export function NoteFilesPanel({
  report,
  onMoved,
}: {
  readonly report: Pick<Report, 'id' | 'status' | 'version'>;
  readonly onMoved: () => void;
}) {
  const { t } = useTranslation('expenses-parity');
  const detail = useSavedDetail();
  const panel = useNoteFilesPanel(report.id, report.version, onMoved);
  const [openFailed, setOpenFailed] = useState(false);
  const { list, files } = panel;
  if (list.status !== 'ready') {
    return (
      <FilesBox count={0} hint={t('files.hint')}>
        <p
          role={list.status === 'failed' ? 'alert' : 'status'}
          className="text-muted-foreground text-sm"
        >
          {t(list.status === 'failed' ? 'files.loadFailed' : 'files.loading')}
        </p>
      </FilesBox>
    );
  }
  const open = OPEN_STATUSES.includes(report.status);
  const count = list.items.length + files.pending.length;
  // Once the note is submitted only Gestione may add (the server's `can.add`): its own hint.
  const hint = list.canAdd && !open ? t('files.hintManager') : t('files.hint');
  return (
    <FilesBox count={list.items.length} hint={hint}>
      {list.items.map((file) => (
        <FileRow
          key={file.documentId}
          title={file.label ?? t('files.unnamed')}
          detail={detail(file)}
          onOpen={() => {
            setOpenFailed(false);
            void openNoteFile(report.id, file.documentId).then((ok) => setOpenFailed(!ok));
          }}
          onRemove={file.can.remove ? () => void panel.remove(file) : undefined}
        />
      ))}
      {openFailed ? (
        <p role="alert" className="text-destructive text-sm">
          {t('files.openFailed')}
        </p>
      ) : null}
      <PendingRows pending={files.pending} onLabel={files.label} onDrop={files.drop} />
      <RefusedRows refused={files.refused} onDismiss={files.dismiss} />
      <SendErrors errors={panel.errors} />
      {list.canAdd ? (
        <>
          <FilePickers
            full={count >= MAX_NOTE_FILES}
            busy={files.preparing || panel.sending}
            onFiles={(chosen) => void files.add(chosen)}
          />
          {files.pending.length > 0 ? (
            <Button type="button" disabled={panel.sending} onClick={() => void panel.send()}>
              {t('files.send', { count: files.pending.length })}
            </Button>
          ) : null}
        </>
      ) : (
        <p role="status" className="text-muted-foreground text-sm">
          {t(list.items.length >= MAX_NOTE_FILES ? 'files.full' : 'files.closed', {
            max: MAX_NOTE_FILES,
          })}
        </p>
      )}
    </FilesBox>
  );
}
